/** Moving projectile head with integrated trailing fade and impact-on-arrival. Composes P1, P2, P6. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { TrailRenderer, TrailRendererConfig } from './TrailRenderer';
import { ParticleBurst, ParticleBurstConfig } from './ParticleBurst';
import { ImpactFlash, ImpactFlashConfig } from './ImpactFlash';
import { ensureCircleTexture } from '../../ui/CrispUtil';
import { distance } from '../../utils/Math';

export interface ProjectileTrailConfig extends VfxPrimitiveConfig {
  headRadius: number;
  headBlendMode: Phaser.BlendModes;
  trailConfig: TrailRendererConfig;
  speed: number;
  onArrival: 'burst' | 'flash' | 'none';
  arrivalBurstConfig?: ParticleBurstConfig;
  arrivalFlashConfig?: ImpactFlashConfig;
}

export class ProjectileTrail extends VfxPrimitive<ProjectileTrailConfig> {
  private head!: Phaser.GameObjects.Image;
  private textureKey!: string;
  private trail!: TrailRenderer;
  private arrivalBurst: ParticleBurst | null = null;
  private arrivalFlash: ImpactFlash | null = null;

  private flying = false;
  private originX = 0;
  private originY = 0;
  private targetX = 0;
  private targetY = 0;
  private currentX = 0;
  private currentY = 0;
  private headDepth = 0;

  /** Callback fired when the projectile reaches its target, before the arrival effect. */
  onArrive?: () => void;

  protected allocatePool(): void {
    this.textureKey = ensureCircleTexture(this.scene, this.config.headRadius);
    this.head = this.createPoolImage(this.textureKey);
    this.head.setBlendMode(this.config.headBlendMode);

    this.trail = new TrailRenderer(this.scene, this.config.trailConfig);

    if (this.config.onArrival === 'burst' && this.config.arrivalBurstConfig) {
      this.arrivalBurst = new ParticleBurst(this.scene, this.config.arrivalBurstConfig);
    }
    if (this.config.onArrival === 'flash' && this.config.arrivalFlashConfig) {
      this.arrivalFlash = new ImpactFlash(this.scene, this.config.arrivalFlashConfig);
    }
  }

  /** Launch projectile from (x, y) toward the current target. */
  play(x: number, y: number, depth: number): void {
    this.flying = true;
    this.originX = x;
    this.originY = y;
    this.currentX = x;
    this.currentY = y;
    this.headDepth = depth;
    this.activate(this.head, x, y, depth);
    this.trail.play(x, y, depth);
  }

  /** Set or update the target position. */
  setTarget(tx: number, ty: number): void {
    this.targetX = tx;
    this.targetY = ty;
  }

  /** Abort the projectile mid-flight with a brief fade. */
  stop(): void {
    if (!this.flying) return;
    this.flying = false;
    this.trail.stop();
    this.scene.tweens.killTweensOf(this.head);
    this.scene.tweens.add({
      targets: this.head,
      alpha: 0,
      duration: 100,
      onComplete: () => this.deactivate(this.head),
    });
  }

  /** Reposition hook called by the orchestrator — drives flight progression. */
  updatePosition(_x: number, _y: number, _depth: number, delta: number): void {
    this.advanceFlight(delta);
  }

  /** Advance the projectile toward its target by one frame. */
  private advanceFlight(delta: number): void {
    if (!this.flying) return;

    const dist = distance(this.currentX, this.currentY, this.targetX, this.targetY);
    const step = this.config.speed * (delta / 1000);

    if (dist <= step) {
      this.arrive();
      return;
    }

    const ratio = step / dist;
    this.currentX += (this.targetX - this.currentX) * ratio;
    this.currentY += (this.targetY - this.currentY) * ratio;

    this.head.setPosition(this.currentX, this.currentY);
    this.trail.updatePosition(this.currentX, this.currentY, this.headDepth, delta);
  }

  private arrive(): void {
    this.flying = false;
    this.trail.stop();
    this.deactivate(this.head);

    this.onArrive?.();

    if (this.config.onArrival === 'burst' && this.arrivalBurst) {
      this.arrivalBurst.play(this.targetX, this.targetY, this.headDepth);
    } else if (this.config.onArrival === 'flash' && this.arrivalFlash) {
      this.arrivalFlash.play(this.targetX, this.targetY, this.headDepth);
    }
  }

  destroy(): void {
    this.trail.destroy();
    this.arrivalBurst?.destroy();
    this.arrivalFlash?.destroy();
    super.destroy();
  }
}
